import { Link } from 'react-router-dom';
import { site } from '../data/site';
import { Icon, Ornament, Reveal, SectionHeading } from '../components/Primitives';
import Seo from '../components/Seo';
import { AssurancePanel, CtaBand, PageHero } from '../components/Shared';

const MILESTONES = [
  {
    year: '1951',
    title: 'The first counter',
    text: 'The family begins trading gold ornaments in Ahmedabad, working to order for a handful of local families.',
  },
  {
    year: '1970s',
    title: 'A name among jewellers',
    text: 'The workshop earns a reputation across Gujarat for closed-set kundan, and the order book grows well beyond Ahmedabad.',
  },
  {
    year: '1990s',
    title: 'Polki at the centre',
    text: 'The workshop specialises in polki — uncut diamond foiled in gold — and the bridal suite becomes the signature.',
  },
  {
    year: '2022',
    title: 'C G Road showroom',
    text: 'The current showroom opens at Ten Eleven Building on C G Road, bringing the workshop and the counter under one roof.',
  },
  {
    year: 'Today',
    title: 'Third generation',
    text: 'Nikhil Soni leads the business, selling to the families who wear the pieces and to jewellers across Gujarat.',
  },
];

const CRAFT = [
  {
    title: 'Polki',
    text: 'Diamond left in its natural flat cleave, closed-set against gold foil. Light returns from the metal behind the stone, which is why polki glows rather than sparkles.',
  },
  {
    title: 'Kundan',
    text: 'Stones held by pressed 24k gold rather than claws. The gold is worked cold, burnished into place a fraction at a time — slow, and impossible to rush.',
  },
  {
    title: 'Meenakari',
    text: 'Vitreous enamel fired into engraved gold, used on the reverse of bridal pieces and the enamelled kada in emerald green and coral.',
  },
];

export default function Heritage() {
  return (
    <>
      <Seo
        title="Heritage"
        description="Nikhil Jewellers has traded in Ahmedabad since 1951 — three generations of closed-set kundan and polki on C G Road."
        image="/assets/lifestyle/lifestyle-07.webp"
      />

      <PageHero
        eyebrow={`Since ${site.since}`}
        title="Seventy-five years, one family"
        lede="Nikhil Jewellers has traded from Ahmedabad since 1951 — first as a single counter, now as a showroom on C G Road."
        image="/assets/lifestyle/lifestyle-07.webp"
        trail={[{ label: 'Home', to: '/' }, { label: 'Heritage' }]}
      />

      {/* Founder */}
      <section className="bg-cream-50 py-16 sm:py-24">
        <div className="shell">
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <div className="relative">
                <div className="arch overflow-hidden bg-royal-800 ring-1 ring-inset ring-gold-500/25">
                  <img
                    src="/assets/team/nikhil-soni.webp"
                    alt="Nikhil Soni, proprietor of Nikhil Jewellers"
                    loading="eager"
                    decoding="async"
                    className="aspect-[3/4] w-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 border border-gold-500/40 bg-cream-50 px-6 py-3 text-center">
                  <p className="font-display text-lg text-royal-900">Nikhil Soni</p>
                  <p className="text-[10.5px] uppercase tracking-wide2 text-royal-700/60">
                    Proprietor · Third generation
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120} className="lg:col-span-7">
              <p className="eyebrow">The People</p>
              <h2 className="display-lg mt-4 text-royal-900">
                A business that never left the counter
              </h2>
              <Ornament className="mt-6 !justify-start" />
              <p className="mt-7 lede">
                Most of what we sell is not designed in a studio. It comes out of
                conversations at the counter — a bride who wants her mother's collar
                remade a size larger, a jeweller in Morbi who needs twelve chandbali
                by Navratri, a groom who wants a kalgi to match a safa he has already
                bought.
              </p>
              <p className="mt-4 lede">
                Because the workshop and the counter sit under one roof, we see the
                same piece from both ends: what it costs to make properly, and what it
                is worth to the person who wears it. That keeps us honest on price and
                unwilling to cut corners on setting.
              </p>
              <p className="mt-4 lede">
                The karigars we work with have been with the family for decades. Polki
                and kundan cannot be hurried, and there is no machine that does it
                better than a person who has done it for thirty years.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/collections" className="btn-primary">
                  See the work
                  <Icon.Arrow className="h-3.5 w-3.5" />
                </Link>
                <Link to="/visit" className="btn-outline">
                  Visit the showroom
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="noise-navy relative overflow-hidden py-20 sm:py-28">
        <div className="shell relative">
          <SectionHeading
            tone="light"
            eyebrow="The Timeline"
            title="How it grew"
            lede="From a single trade counter to a showroom that serves families and jewellers alike."
          />

          <ol className="mt-16 space-y-px">
            {MILESTONES.map((m, i) => (
              <Reveal
                as="li"
                key={m.year}
                delay={i * 80}
                className="group grid gap-4 border-t border-cream-50/10 py-8 sm:grid-cols-12 sm:gap-8"
              >
                <div className="sm:col-span-3">
                  <p className="font-display text-3xl font-light text-gold-300">{m.year}</p>
                </div>
                <div className="sm:col-span-9">
                  <h3 className="font-display text-2xl font-normal text-cream-50">{m.title}</h3>
                  <p className="mt-2.5 max-w-2xl text-[14.5px] leading-relaxed text-cream-200/70">
                    {m.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Craft */}
      <section className="bg-cream-100 py-20 sm:py-28">
        <div className="shell">
          <SectionHeading
            eyebrow="The Craft"
            title="Three techniques we keep alive"
            lede="All three are older than the shop, and all three are done the same way now as they were in 1951."
          />

          <div className="mt-14 grid gap-px bg-royal-800/10 md:grid-cols-3">
            {CRAFT.map((c, i) => (
              <Reveal key={c.title} delay={i * 100} className="bg-cream-50 p-9">
                <p className="font-display text-5xl font-light text-gold-500/35">
                  0{i + 1}
                </p>
                <h3 className="mt-4 font-display text-2xl font-normal text-royal-900">
                  {c.title}
                </h3>
                <Ornament className="mt-5 !justify-start" />
                <p className="mt-5 text-[14px] leading-relaxed text-royal-700/70">{c.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Showroom strip */}
      <section className="bg-cream-50 py-16 sm:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="The Showroom"
            title="Ten Eleven Building, C G Road"
            lede="Cases arranged by occasion rather than by metal, so a bridal suite can be seen together."
          />
          {/* Interior-only frames carry baked-in campaign copy; these show the
              same room with the jewellery actually being worn. */}
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {[
              ['lifestyle-09', 'Customer wearing a polki choker in the Nikhil Jewellers showroom'],
              ['lifestyle-08', 'Model in a maroon saree wearing an emerald and gold suite in the showroom'],
              ['lifestyle-05', 'Model wearing a polki choker at the showroom counter'],
            ].map(([img, alt], i) => (
              <Reveal key={img} delay={i * 90}>
                <div className="aspect-[4/5] overflow-hidden bg-royal-900 ring-1 ring-inset ring-royal-800/10">
                  <img
                    src={`/assets/lifestyle/${img}.webp`}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-[1300ms]
                      ease-luxe hover:scale-[1.05]"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AssurancePanel />
      <CtaBand />
    </>
  );
}
